import axios from 'axios';

// Resolve API base URL with sensible defaults
const resolveApiBaseUrl = (): string => {
  const fromEnv = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
  if (fromEnv && /^https?:\/\//i.test(fromEnv)) return fromEnv;
  if (fromEnv) return `https://${fromEnv}`; // allow host-only values in env
  return import.meta.env.PROD ? window.location.origin : 'http://localhost:3001';
};

const API_BASE_URL = resolveApiBaseUrl();

// Create axios instance with default config
const api = axios.create({
  baseURL: API_BASE_URL,
  timeout: 300000, // 5 minutes for AI processing
  headers: {
    'Content-Type': 'application/json',
  },
});

// Request interceptor to add session ID
api.interceptors.request.use(
  (config) => {
    const sessionId = localStorage.getItem('vtechSessionId');
    if (sessionId) {
      config.headers['X-Session-ID'] = sessionId;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response) => response,
  (error) => {
    console.error('API Error:', error);
    
    // Handle specific error cases
    if (error.response?.status === 408) {
      // Timeout error
      return Promise.reject(new Error('Request timeout - processing took too long'));
    }
    
    if (error.response?.status === 429) {
      // Rate limit error
      return Promise.reject(new Error('Too many requests. Please try again later.'));
    }
    
    // Generic error handling
    const errorMessage = error.response?.data?.error || error.message || 'An unexpected error occurred';
    return Promise.reject(new Error(errorMessage));
  }
);

// API endpoints
export const endpoints = {
  // Resume analysis
  analyzeResume: '/api/analyze-resume',
  parseResume: '/api/parse-resume',
  
  // Domain services
  checkDomain: '/api/check-domain',
  
  // AI tools
  capabilityStatement: '/api/capability-statement',
  digitalModernization: '/api/digital-modernization/assess',
  customAppMigration: '/api/custom-app-migration/assess',
  itsmToServiceNow: '/api/itsm-servicenow/assess',
  infrastructureMigration: '/api/infrastructure-migration/assess',
  caseManagementToSalesforce: '/api/case-management-salesforce/assess',
  resumedownloadmode: 'api/resume-download-click',
  tooldownloadmode: 'api/tool-download',
  
  // Health check
  health: '/health',
} as const;

// API functions
export const apiService = {
  // Generic HTTP methods
  async get(url: string, config?: any) {
    const response = await api.get(url, config);
    return response.data;
  },
  
  async post(url: string, data?: any, config?: any) {
    const response = await api.post(url, data, config);
    return response.data;
  },
  
  async put(url: string, data?: any, config?: any) {
    const response = await api.put(url, data, config);
    return response.data;
  },
  
  async delete(url: string, config?: any) {
    const response = await api.delete(url, config);
    return response.data;
  },
  
  // Resume analysis
  async analyzeResume(data: { resumeText: string; mode: string; jobDescription?: string }) {
    const response = await api.post(endpoints.analyzeResume, data);
    return response.data;
  },
  
  async parseResume(data: { resumeText: string }) {
    const response = await api.post(endpoints.parseResume, data);
    return response.data;
  },
  
  // Domain services
  async checkDomain(data: { domain: string }) {
    const response = await api.post(endpoints.checkDomain, data);
    return response.data;
  },
  
  // AI tools
  async generateCapabilityStatement(data: any) {
    const response = await api.post(endpoints.capabilityStatement, data);
    return response.data;
  },
  
  async processDigitalModernization(data: any) {
    const response = await api.post(endpoints.digitalModernization, data);
    return response.data;
  },
  
  async processCustomAppMigration(data: any) {
    const response = await api.post(endpoints.customAppMigration, data);
    return response.data;
  },
  
  async processITSMToServiceNow(data: any) {
    const response = await api.post(endpoints.itsmToServiceNow, data);
    return response.data;
  },
  
  async processInfrastructureMigration(data: any) {
    const response = await api.post(endpoints.infrastructureMigration, data);
    return response.data;
  },
  
  async processCaseManagementToSalesforce(data: any) {
    const response = await api.post(endpoints.caseManagementToSalesforce, data);
    return response.data;
  },
  
  async processEcoAssessment(data: any) {
    const response = await api.post(endpoints.ecoAssessment, data);
    return response.data;
  },
  
  async processVSTransformation(data: any) {
    const response = await api.post(endpoints.vsTransformation, data);
    return response.data;
  },
  
  // Health check
  async healthCheck() {
    const response = await api.get(endpoints.health);
    return response.data;
  },
};

// Export the axios instance as apiClient for consistency
export const apiClient = api;

export default api;
